import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "@/lib/useTranslations";
import type { KeyboardEvent } from "react";

import { PlacesLibrarySearchForm } from "@/components/ui/admin/places/PlacesLibrarySearchForm";
import { PlacesLibraryQuickAddRow } from "@/components/ui/admin/places/PlacesLibraryQuickAddRow";
import { LibraryResultsFreezeControl } from "@/components/ui/admin/LibraryResultsFreezeControl";
import { LibrarySortableHeader } from "@/components/ui/admin/LibrarySortableHeader";
import { LibraryChecklistProgress } from "@/components/ui/admin/LibraryChecklistProgress";
import { LibraryPagination } from "@/components/ui/admin/LibraryPagination";
import { defaultLocale } from "@/i18n/config";
import type { ContentStatus } from "@/core/services/types/article.types";
import type {
  PlaceAdminListItem,
  PlaceLibraryTab,
  PlaceLibraryTabCounts,
  PlaceLibrarySortState,
} from "@/core/services/types/place.types";
import {
  PLACE_LIBRARY_TABS,
  buildPlaceLibraryHref,
  buildPlaceLibrarySortHref,
  getPlaceEditorialChecklistProgress,
  getPlaceSharePath,
} from "@/core/services/types/place.types";
import type { LibraryPageSize } from "@/core/services/types/library-pagination.types";
import type { LibrarySortDirection } from "@/core/services/types/library-sort.types";

interface PlacesLibraryPanelProps {
  items: PlaceAdminListItem[];
  total: number;
  page: number;
  pageSize: LibraryPageSize;
  tab: PlaceLibraryTab;
  tabCounts: PlaceLibraryTabCounts;
  searchQuery: string;
  sort: PlaceLibrarySortState;
  isRefreshing?: boolean;
  isFrozen?: boolean;
  onToggleResultsFreeze?: () => void;
  canFreezeResults?: boolean;
}

function formatUpdatedAt(value: string): string {
  return new Intl.DateTimeFormat(defaultLocale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function formatCreatedAt(value: string): string {
  return formatUpdatedAt(value);
}

function buildLibraryHref(
  tab: PlaceLibraryTab,
  targetPage: number,
  searchQuery: string,
  sort: PlaceLibrarySortState,
  currentPageSize: LibraryPageSize,
): string {
  return buildPlaceLibraryHref({
    tab,
    page: targetPage,
    pageSize: currentPageSize,
    searchQuery,
    sort,
  });
}

function getSortDirection(sort: PlaceLibrarySortState): LibrarySortDirection {
  return sort?.direction ?? "desc";
}

function PlaceTypeLabel({ place }: { place: PlaceAdminListItem }) {
  const t = useTranslations("places.library.placeTypes");

  if (!place.place_type) return "—";

  return t(place.place_type);
}

function StatusBadge({ place }: { place: PlaceAdminListItem }) {
  const t = useTranslations("places.library.statuses");

  const styles: Record<ContentStatus, string> = {
    SIGNAL: "bg-violet-50 text-violet-700",
    DRAFT: "bg-neutral-100 text-neutral-600",
    PENDING_REVIEW: "bg-amber-50 text-amber-700",
    SCHEDULED: "bg-amber-50 text-amber-800",
    PUBLISHED: "bg-emerald-50 text-emerald-700",
    ARCHIVED: "bg-neutral-100 text-neutral-500",
  };

  return (
    <span
      className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${styles[place.status]}`}
    >
      {t(place.status)}
    </span>
  );
}

function openPlaceFromKeyboard(
  event: KeyboardEvent<HTMLTableRowElement>,
  onOpen: () => void,
) {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    onOpen();
  }
}

export function PlacesLibraryPanel({
  items,
  total,
  page,
  pageSize,
  tab,
  tabCounts,
  searchQuery,
  sort,
  isRefreshing = false,
  isFrozen = false,
  onToggleResultsFreeze,
  canFreezeResults = false,
}: PlacesLibraryPanelProps) {
  const t = useTranslations("places.library");
  const tCommon = useTranslations("common");
  const navigate = useNavigate();

  const buildPaginationHref = (targetPage: number, targetPageSize = pageSize) =>
    buildLibraryHref(tab, targetPage, searchQuery, sort, targetPageSize);

  return (
    <section className="flex w-full min-w-0 flex-col gap-4">
      <PlacesLibrarySearchForm
        tab={tab}
        searchQuery={searchQuery}
        sort={sort}
        pageSize={pageSize}
      />

      <div className="w-full min-w-0 overflow-hidden rounded-xl border border-black/10 bg-white shadow-sm">
      <nav
        aria-label={t("statusNav")}
        className="border-b border-black/10"
      >
        <ul className="flex items-center gap-1 overflow-x-auto px-4">
          {PLACE_LIBRARY_TABS.map((tabStatus) => {
            const isActive = tab === tabStatus;

            return (
              <li key={tabStatus} className="shrink-0">
                <Link
                  to={buildPlaceLibraryHref({
                    tab: tabStatus,
                    page: 1,
                    pageSize,
                    searchQuery,
                  })}
                  className={`inline-flex border-b-2 px-3 py-3 text-sm transition-colors ${
                    isActive
                      ? "border-whart-dark font-medium text-whart-text"
                      : "border-transparent text-neutral-600 hover:border-black/10 hover:text-whart-text"
                  }`}
                  aria-current={isActive ? "page" : undefined}
                >
                  {t(`statuses.${tabStatus}`)}
                  <span className="ml-1.5 text-neutral-400">
                    ({tabCounts[tabStatus]})
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="flex items-center justify-between gap-3 border-b border-black/10 px-4 py-3">
        <div className="inline-flex flex-wrap items-center gap-2 text-sm text-neutral-600">
          <p className="inline-flex items-center gap-2">
            {t("placeCount", { count: total })}
            {isRefreshing ? (
              <span
                aria-label="Chargement"
                className="inline-block size-3.5 animate-spin rounded-full border-2 border-zinc-900 border-r-transparent"
              />
            ) : null}
          </p>
          {isFrozen ? (
            <span className="inline-flex rounded-xl bg-amber-100 px-2 py-0.5 text-xs font-medium text-zinc-900">
              {tCommon("library.frozenResultsBadge")}
            </span>
          ) : null}
        </div>
        {onToggleResultsFreeze ? (
          <LibraryResultsFreezeControl
            isFrozen={isFrozen}
            onToggle={onToggleResultsFreeze}
            disabled={!isFrozen && !canFreezeResults}
          />
        ) : null}
      </div>

      <div className="w-full min-w-0 overflow-x-auto">
          <table className="w-full min-w-[80rem] table-fixed border-collapse text-left text-sm">
            <colgroup>
              <col className="w-[22%]" />
              <col className="w-[9%]" />
              <col className="w-[12%]" />
              <col className="w-[10%]" />
              <col className="w-[9%]" />
              <col className="w-[13%]" />
              <col className="w-[13%]" />
              <col className="w-[12%]" />
            </colgroup>
            <thead className="sticky top-0 z-10 bg-zinc-50">
              <tr className="border-b border-black/10 text-xs font-medium uppercase tracking-wider text-neutral-500">
                <LibrarySortableHeader
                  label={t("nameColumn")}
                  column="name"
                  activeColumn={sort?.column}
                  direction={getSortDirection(sort)}
                  href={buildPlaceLibrarySortHref({
                    tab,
                    searchQuery,
                    pageSize,
                    sort,
                    nextColumn: "name",
                  })}
                  className="border-r border-black/5 px-4 py-3 text-left"
                />
                <th className="border-r border-black/5 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-neutral-500">
                  {tCommon("library.checklistColumn")}
                </th>
                <LibrarySortableHeader
                  label={t("typeColumn")}
                  column="place_type"
                  activeColumn={sort?.column}
                  direction={getSortDirection(sort)}
                  href={buildPlaceLibrarySortHref({
                    tab,
                    searchQuery,
                    pageSize,
                    sort,
                    nextColumn: "place_type",
                  })}
                  className="border-r border-black/5 px-4 py-3 text-left"
                />
                <LibrarySortableHeader
                  label={t("cityColumn")}
                  column="city"
                  activeColumn={sort?.column}
                  direction={getSortDirection(sort)}
                  href={buildPlaceLibrarySortHref({
                    tab,
                    searchQuery,
                    pageSize,
                    sort,
                    nextColumn: "city",
                  })}
                  className="border-r border-black/5 px-4 py-3 text-left"
                />
                <LibrarySortableHeader
                  label={t("zipCodeColumn")}
                  column="zip_code"
                  activeColumn={sort?.column}
                  direction={getSortDirection(sort)}
                  href={buildPlaceLibrarySortHref({
                    tab,
                    searchQuery,
                    pageSize,
                    sort,
                    nextColumn: "zip_code",
                  })}
                  className="border-r border-black/5 px-4 py-3 text-left"
                />
                <LibrarySortableHeader
                  label={t("createdAtColumn")}
                  column="created_at"
                  activeColumn={sort?.column}
                  direction={getSortDirection(sort)}
                  href={buildPlaceLibrarySortHref({
                    tab,
                    searchQuery,
                    pageSize,
                    sort,
                    nextColumn: "created_at",
                  })}
                  className="border-r border-black/5 px-4 py-3 text-left"
                />
                <LibrarySortableHeader
                  label={t("updatedAtColumn")}
                  column="updated_at"
                  activeColumn={sort?.column}
                  direction={getSortDirection(sort)}
                  href={buildPlaceLibrarySortHref({
                    tab,
                    searchQuery,
                    pageSize,
                    sort,
                    nextColumn: "updated_at",
                  })}
                  className="border-r border-black/5 px-4 py-3 text-left"
                />
                <LibrarySortableHeader
                  label={tCommon("status")}
                  column="status"
                  activeColumn={sort?.column}
                  direction={getSortDirection(sort)}
                  href={buildPlaceLibrarySortHref({
                    tab,
                    searchQuery,
                    pageSize,
                    sort,
                    nextColumn: "status",
                  })}
                  className="px-4 py-3 text-left"
                />
              </tr>
            </thead>
            <tbody>
              <PlacesLibraryQuickAddRow />
              {items.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-8 text-center text-sm text-neutral-500"
                  >
                    {searchQuery ? t("emptySearch") : t("empty")}
                  </td>
                </tr>
              ) : (
                items.map((place) => {
                const editHref = getPlaceSharePath(place.id);
                const openPlace = () => navigate(editHref);

                return (
                  <tr
                    key={place.id}
                    tabIndex={0}
                    role="link"
                    aria-label={t("editPlace", { name: place.name })}
                    className="cursor-pointer border-b border-black/5 transition-colors even:bg-zinc-50/50 hover:bg-amber-50/40 focus-visible:bg-amber-50/40 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-whart-dark"
                    onClick={openPlace}
                    onKeyDown={(keyboardEvent) =>
                      openPlaceFromKeyboard(keyboardEvent, openPlace)
                    }
                  >
                    <td className="border-r border-black/5 px-4 py-3 font-medium text-whart-text">
                      <span className="line-clamp-2">{place.name}</span>
                    </td>
                    <td className="border-r border-black/5 px-4 py-3">
                      <LibraryChecklistProgress
                        progress={getPlaceEditorialChecklistProgress(place)}
                      />
                    </td>
                    <td className="border-r border-black/5 px-4 py-3 text-neutral-600">
                      <PlaceTypeLabel place={place} />
                    </td>
                    <td className="border-r border-black/5 px-4 py-3 text-neutral-600">
                      <span className="block truncate">{place.city ?? "—"}</span>
                    </td>
                    <td className="border-r border-black/5 px-4 py-3 whitespace-nowrap text-neutral-600">
                      {place.zip_code?.trim() || "—"}
                    </td>
                    <td className="border-r border-black/5 px-4 py-3 whitespace-nowrap text-neutral-600">
                      {formatCreatedAt(place.created_at)}
                    </td>
                    <td className="border-r border-black/5 px-4 py-3 whitespace-nowrap text-neutral-600">
                      {formatUpdatedAt(place.updated_at)}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge place={place} />
                    </td>
                  </tr>
                );
              })
              )}
            </tbody>
          </table>
        </div>

      <LibraryPagination
        page={page}
        pageSize={pageSize}
        total={total}
        buildHref={buildPaginationHref}
      />
      </div>
    </section>
  );
}
